// ../components/useHorizontalDragScroll.js
import { useCallback, useEffect, useRef } from "react";

/**
 * Horizontal grab-scroll + (optional) mouse-wheel → horizontal scroll
 * - Prevents accidental Link clicks when user drags past threshold
 * - Uses a native wheel listener with passive:false to allow preventDefault safely
 */
export function useHorizontalDragScroll({ dragThresholdPx = 6 } = {}) {
  const ref = useRef(null);

  const state = useRef({
    isDown: false,
    isDragging: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    pointerId: null,
  });

  const onPointerDown = useCallback((e) => {
    const el = ref.current;
    if (!el) return;

    // Only left click for mouse (touch/pen ok)
    if (e.pointerType === "mouse" && e.button !== 0) return;

    state.current.isDown = true;
    state.current.isDragging = false;
    state.current.startX = e.clientX;
    state.current.startY = e.clientY;
    state.current.startScrollLeft = el.scrollLeft;
    state.current.pointerId = e.pointerId;

    try {
      el.setPointerCapture(e.pointerId);
    } catch {}

    el.classList.add("cursor-grabbing");
    el.classList.remove("cursor-grab");
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      if (!state.current.isDown) return;

      const dx = e.clientX - state.current.startX;
      const dy = e.clientY - state.current.startY;

      if (!state.current.isDragging) {
        const moved = Math.max(Math.abs(dx), Math.abs(dy));
        if (moved < dragThresholdPx) return;

        // Only start dragging if horizontal intent
        if (Math.abs(dx) >= Math.abs(dy)) {
          state.current.isDragging = true;
        } else {
          // vertical intent: stop, let page scroll normally
          state.current.isDown = false;
          el.classList.remove("cursor-grabbing");
          el.classList.add("cursor-grab");
          return;
        }
      }

      // Pointer events are not passive -> preventDefault is fine here
      e.preventDefault();
      el.scrollLeft = state.current.startScrollLeft - dx;
    },
    [dragThresholdPx]
  );

  const endDrag = useCallback((e) => {
    const el = ref.current;
    if (!el) return;

    state.current.isDown = false;

    try {
      if (state.current.pointerId != null) {
        el.releasePointerCapture(state.current.pointerId);
      }
    } catch {}

    el.classList.remove("cursor-grabbing");
    el.classList.add("cursor-grab");
  }, []);

  // Prevent Link clicks after a drag
  const onClickCapture = useCallback((e) => {
    if (state.current.isDragging) {
      e.preventDefault();
      e.stopPropagation();
      state.current.isDragging = false;
    }
  }, []);

  // Native wheel listener with passive:false
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onWheel = (e) => {
      // only if horizontal overflow exists
      const canScrollX = el.scrollWidth > el.clientWidth;
      if (!canScrollX) return;

      // Map vertical wheel to horizontal scroll for mouse users
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        // allow preventDefault because passive:false
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, []);

  return {
    ref,
    bind: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onPointerLeave: endDrag,
      onClickCapture,
    },
  };
}
